/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { FC, useRef, useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Calendar, CalendarMode } from '@app/components/index';
import isDate from 'lodash/isDate';
import { Interval as Range, set } from 'date-fns';
import './form-input.scss';
import { formatDate } from '@app/utils';
import CalendarIcon from './CalendarIcon';

type TFormCalendar = {
  value: Date | Range | null;
  setValue: Dispatch<SetStateAction<Date | Range | null>>;
  placeholder?: string;
  isMobile: boolean;
  className?: string;
  minDate?: Date;
};

const endOfDay = (date: Date | number): Date => {
  return set(date, { hours: 23, minutes: 59, seconds: 59 });
};

const FormCalendarRange: FC<TFormCalendar> = ({
  value,
  placeholder = '',
  isMobile,
  setValue,
  className = '',
  minDate = new Date(),
}) => {
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [visibleValue, setVisibleValue] = useState('');

  const handleHeadClick: () => void = () => {
    setIsCalendarVisible((prev) => !prev);
  };

  const onDateSelected: (date: Date | Range) => void = (date: Date | Range) => {
    const range = isDate(date)
      ? {
          start: date,
          end: endOfDay(date),
        }
      : {
          start: date.start,
          end: endOfDay(date.end),
        };
    setValue(range);
  };

  useEffect(() => {
    const handleClick: (e: MouseEvent) => void = (e: MouseEvent) => {
      const { target } = e;
      if (target instanceof Node && !inputRef.current?.contains(target) && !calendarRef.current?.contains(target)) {
        setIsCalendarVisible(false);
      }
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    if (value) {
      if (isDate(value)) {
        setVisibleValue(formatDate(value));
      } else {
        setVisibleValue(`${formatDate(value.start as Date)}-${formatDate(value.end as Date)}`);
      }
    } else {
      setVisibleValue('');
    }
  }, [value]);

  return (
    <div className={`form-calendar ${className}`}>
      <div className="form-calendar__input-wrap" ref={inputRef} onClick={handleHeadClick}>
        <div className="form-calendar__input">{visibleValue || placeholder}</div>
        <div className="form-calendar__icon">
          <CalendarIcon />
        </div>
      </div>
      {isCalendarVisible && (
        <div className="form-calendar__calendar-wrap" ref={calendarRef}>
          <Calendar
            minDate={minDate}
            className="form-calendar-component__calendar"
            headClassName="form-calendar-component__head"
            bodyClassName="form-calendar-component__body"
            daysOfWeekClassName="form-calendar-component__days-of-week"
            dayOfWeekClassName="form-calendar-component__cell"
            dayClassName="form-calendar-component__cell"
            view={isMobile ? CalendarMode.WEEK : CalendarMode.MONTH}
            onDateSelected={onDateSelected}
            onRangeSelected={onDateSelected}
            rangeMode
            preselectedDate={isDate(value) ? value : (value?.start as Date) || new Date()}
            preselectedRange={isDate(value) ? { start: value, end: value } : (value as Range)}
          />
        </div>
      )}
    </div>
  );
};

export default FormCalendarRange;
